import { useState, useCallback } from 'react';
import { Box, CircularProgress, Alert, Paper, Snackbar, useTheme } from '@mui/material';
import { useBpmnModeler } from '../../hooks/useBpmnModeler';
import { BpmnEditorToolbar } from './BpmnEditorToolbar';
import { IntegratedPropertiesPanel } from '../PropertiesPanel';
import type { BpmnEditorProps } from '../../types/bpmn.types';

import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import 'bpmn-js/dist/assets/bpmn-js.css';

// Dark mode styles for BPMN editor
const darkModeStyles = {
  // Canvas background
  '& .djs-container': {
    backgroundColor: '#1e1e1e !important',
  },
  // SVG elements
  '& .djs-visual > *': {
    stroke: '#e0e0e0 !important',
  },
  '& .djs-visual text': {
    fill: '#e0e0e0 !important',
  },
  // Connections
  '& .djs-connection path': {
    stroke: '#90caf9 !important',
  },
  '& .djs-connection marker path': {
    fill: '#90caf9 !important',
    stroke: '#90caf9 !important',
  },
  // Shape fills
  '& .djs-shape .djs-visual > rect, & .djs-shape .djs-visual > circle, & .djs-shape .djs-visual > polygon': {
    fill: '#2d2d2d !important',
  },
  // Events (start, end, intermediate)
  '& .bpmn-icon-start-event-none, & .bpmn-icon-end-event-none': {
    color: '#e0e0e0 !important',
  },
  // Selection outline
  '& .djs-outline': {
    stroke: '#90caf9 !important',
  },
  // Palette
  '& .djs-palette': {
    backgroundColor: '#2d2d2d !important',
    borderColor: '#424242 !important',
  },
  '& .djs-palette .entry': {
    color: '#e0e0e0 !important',
  },
  '& .djs-palette .entry:hover': {
    backgroundColor: '#424242 !important',
  },
  // Context pad
  '& .djs-context-pad': {
    backgroundColor: '#2d2d2d !important',
  },
  '& .djs-context-pad .entry': {
    backgroundColor: '#2d2d2d !important',
  },
  '& .djs-context-pad .entry:hover': {
    backgroundColor: '#424242 !important',
  },
  // Popup menu
  '& .djs-popup': {
    backgroundColor: '#2d2d2d !important',
    borderColor: '#424242 !important',
  },
  '& .djs-popup .entry': {
    color: '#e0e0e0 !important',
  },
  '& .djs-popup .entry:hover': {
    backgroundColor: '#424242 !important',
  },
  // Direct editing
  '& .djs-direct-editing-parent': {
    backgroundColor: '#2d2d2d !important',
  },
  '& .djs-direct-editing-content': {
    color: '#e0e0e0 !important',
    backgroundColor: '#2d2d2d !important',
  },
};

export function BpmnEditor({
  xml,
  processDefinitionKey,
  keyId,
  isOwnProcess = false,
  onSave,
  onChange,
  height = 600,
  showPropertiesPanel = true,
}: BpmnEditorProps & { showPropertiesPanel?: boolean }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    containerRef,
    isLoading,
    error,
    isDirty,
    canUndo,
    canRedo,
    selectedElement,
    getXml,
    undo,
    redo,
    save,
    zoomIn,
    zoomOut,
    fitViewport,
    exportSvg,
    createNewDiagram,
    updateElementProperty,
    updateElementName,
    updateElementDocumentation,
    updateAmtExtension,
  } = useBpmnModeler({
    xml,
    processDefinitionKey,
    keyId,
    isOwnProcess,
    onChange,
  });

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      if (onSave) {
        const currentXml = await getXml();
        if (currentXml) {
          onSave(currentXml);
          setSnackbar({
            open: true,
            message: 'Diagramm erfolgreich gespeichert',
            severity: 'success',
          });
        }
      } else {
        const success = await save();
        if (success) {
          setSnackbar({
            open: true,
            message: 'Diagramm erfolgreich gespeichert',
            severity: 'success',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Fehler beim Speichern',
            severity: 'error',
          });
        }
      }
    } finally {
      setIsSaving(false);
    }
  }, [onSave, getXml, save]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: typeof height === 'number' ? height : height,
        overflow: 'hidden',
      }}
    >
      <BpmnEditorToolbar
        onSave={handleSave}
        onUndo={undo}
        onRedo={redo}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitViewport={fitViewport}
        onExportSvg={exportSvg}
        onNewDiagram={createNewDiagram}
        canUndo={canUndo}
        canRedo={canRedo}
        isDirty={isDirty}
        isSaving={isSaving}
        processDefinitionKey={processDefinitionKey}
      />

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDarkMode ? 'rgba(30, 30, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            height: '100%',
            minWidth: 0,
            '& .bjs-powered-by': {
              display: 'none',
            },
            ...(isDarkMode && darkModeStyles),
          }}
        />

        {showPropertiesPanel && (
          <Box
            sx={{
              width: 480,
              minWidth: 480,
              borderLeft: 1,
              borderColor: 'divider',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <IntegratedPropertiesPanel
              element={selectedElement}
              onUpdateProperty={updateElementProperty}
              onUpdateName={updateElementName}
              onUpdateDocumentation={updateElementDocumentation}
              onUpdateAmtExtension={updateAmtExtension}
            />
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Paper>
  );
}
